<template>
  <div class="card" style="margin-top:12px;">
    <h3>{{ form.id ? 'Edit Lead / Inquiry' : 'New Lead' }}</h3>

    <!-- Permanent Customer ID (read-only once created) -->
    <div class="row" v-if="form.leadCode" style="margin-top:4px;">
      <div class="col">
        <label class="helper">Customer ID</label>
        <input class="input" :value="form.leadCode" disabled>
      </div>
    </div>

    <!-- Basic identity -->
    <div class="row" style="margin-top:8px;">
      <div class="col">
        <label class="helper">Name *</label>
        <input class="input" v-model.trim="form.name">
      </div>
      <div class="col">
        <label class="helper">Phone</label>
        <input class="input" v-model.trim="form.phone" placeholder="+91 98XXXXXX89">
      </div>
    </div>

    <div class="row" style="margin-top:8px;">
      <div class="col">
        <label class="helper">Email</label>
        <input class="input" v-model.trim="form.email" placeholder="name@example.com">
      </div>
      <div class="col">
        <label class="helper">Address</label>
        <input class="input" v-model.trim="form.address">
        <div class="helper" style="margin-top:4px;">
          * Phone or Address must be provided
        </div>
      </div>
    </div>

    <!-- Products & cause -->
    <div class="row" style="margin-top:8px;">
      <div class="col">
        <label class="helper">Product(s) * (select all that apply)</label>
        <div class="row" style="gap:16px; align-items:center;">
          <label><input type="checkbox" :checked="hasProduct('kitchen')" @change="toggleProduct('kitchen')"> Kitchen</label>
          <label><input type="checkbox" :checked="hasProduct('wardrobe')" @change="toggleProduct('wardrobe')"> Wardrobe</label>
          <label><input type="checkbox" :checked="hasProduct('vanity')" @change="toggleProduct('vanity')"> Vanity</label>
        </div>
      </div>
      <div class="col">
        <label class="helper">Cause of lead *</label>
        <select class="input" v-model="form.cause">
          <option disabled value="">Select</option>
          <option value="family">Family / Relative</option>
          <option value="friend">Friend / Reference</option>
          <option value="cold_call">Cold Call</option>
          <option value="walk_in">Walk-in</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
          <option value="google_ads">Google Ads</option>
          <option value="site_visit">Site Visit</option>
          <option value="builder">Builder / Contractor</option>
          <option value="architect">Architect</option>
          <option value="other">Other</option>
        </select>
        <input v-if="form.cause==='other'" class="input" style="margin-top:6px"
               v-model.trim="form.causeOther" placeholder="Describe other source...">
      </div>
    </div>

    <!-- Business status -->
    <div class="row" style="margin-top:12px;">
      <div class="col">
        <label class="helper">Business status *</label>
        <div class="row" style="gap:18px; align-items:center;">
          <label><input type="radio" value="follow_up" v-model="form.status"> Follow-up</label>
          <label><input type="radio" value="matured"   v-model="form.status"> Inquiry</label>
          <label><input type="radio" value="dead"      v-model="form.status"> Dead</label>
        </div>

        <!-- Follow-up controls -->
        <div v-if="form.status==='follow_up'" style="margin-top:8px;">
          <label class="helper">Next follow-up date *</label>
          <input type="date" class="input"
                 v-model="form.followUpDate"
                 :min="today"
                 @blur="fixPastDate">

          <div class="row" style="gap:8px; margin-top:6px;">
            <button class="btn secondary small" @click="pickToday">Today</button>
            <button class="btn secondary small" @click="addDays(1)">+1 day</button>
            <button class="btn secondary small" @click="addDays(3)">+3 days</button>
            <button class="btn secondary small" @click="addDays(7)">Next week</button>
          </div>

          <div style="margin-top:8px;">
            <label class="helper">Follow-up note (optional)</label>
            <input class="input" v-model.trim="form.followUpNote" placeholder="Example: Called, shared brochure, waiting for site visit…">
          </div>
        </div>

        <!-- Dead reason -->
        <div v-if="form.status==='dead'" style="margin-top:8px;">
          <label class="helper">Reason *</label>
          <select class="input" v-model="form.deadReason">
            <option disabled value="">Select</option>
            <option value="expensive">Expensive / price too high</option>
            <option value="range">Not enough product range</option>
            <option value="time">Time constraint</option>
            <option value="other">Other</option>
          </select>
          <input v-if="form.deadReason==='other'" class="input" style="margin-top:6px"
                 v-model.trim="form.deadOther" placeholder="Write other reason...">
        </div>
      </div>
    </div>

    <div class="row" style="margin-top:8px;">
      <div class="col">
        <label class="helper">Notes</label>
        <textarea class="input" rows="3" v-model.trim="form.notes"></textarea>
      </div>
    </div>

    <!-- PRE-INQUIRY DOCS: available as soon as lead exists -->
    <div class="card" style="margin-top:14px;background:#fafafa;border:1px solid #e5e7eb;" v-if="form.id">
      <div class="row" style="align-items:center;gap:10px;">
        <b>Site Docs</b>

        <span class="badge" :class="docsBadgeClass">
          {{ docsBadgeText }}
        </span>

        <span class="helper" style="margin-left:auto;">
          Photos: <b>{{ docs.sitePhotos }}</b> • Measurements: <b>{{ docs.measurements }}</b> • Drawings: <b>{{ docs.drawings }}</b>
        </span>

        <button class="btn secondary small" @click="refreshDocs" :disabled="docsBusy">
          {{ docsBusy ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>

      <div class="helper" style="margin-top:6px;" v-if="docsHintText" :style="{color: docsHintColor}">
        {{ docsHintText }}
      </div>

      <div class="row" style="margin-top:10px; gap:12px; flex-wrap:wrap;">
        <div style="flex:1 1 300px;">
          <h4 style="margin:0 0 6px;">Site Photos (required)</h4>
          <div class="row" style="gap:8px;align-items:center;">
            <input type="file" @change="pickFile($event,'SITE_PHOTO')" accept=".png,.jpg,.jpeg,.webp,.pdf">
            <button class="btn small" :disabled="docsBusy || !sel.SITE_PHOTO" @click="upload('SITE_PHOTO')">Upload</button>
          </div>
          <ul class="helper" v-if="photos.length" style="margin-top:6px;">
            <li v-for="d in photos" :key="d.id">
              <a :href="d.url" target="_blank" rel="noopener">{{ d.fileName }}</a>
              <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
            </li>
          </ul>
          <div v-else class="helper">No site photos uploaded.</div>
        </div>

        <div style="flex:1 1 300px;">
          <h4 style="margin:0 0 6px;">Measurements (required)</h4>
          <div class="row" style="gap:8px;align-items:center;">
            <input type="file" @change="pickFile($event,'MEASUREMENT')" accept=".png,.jpg,.jpeg,.webp,.pdf">
            <button class="btn small" :disabled="docsBusy || !sel.MEASUREMENT" @click="upload('MEASUREMENT')">Upload</button>
          </div>
          <ul class="helper" v-if="measurements.length" style="margin-top:6px;">
            <li v-for="d in measurements" :key="d.id">
              <a :href="d.url" target="_blank" rel="noopener">{{ d.fileName }}</a>
              <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
            </li>
          </ul>
          <div v-else class="helper">No measurements uploaded.</div>
        </div>

        <div style="flex:1 1 300px;">
          <h4 style="margin:0 0 6px;">Drawings (optional)</h4>
          <div class="row" style="gap:8px;align-items:center;">
            <input type="file" @change="pickFile($event,'DRAWING')" accept=".pdf,.png,.jpg,.jpeg,.svg">
            <button class="btn small" :disabled="docsBusy || !sel.DRAWING" @click="upload('DRAWING')">Upload</button>
          </div>
          <ul class="helper" v-if="drawings.length" style="margin-top:6px;">
            <li v-for="d in drawings" :key="d.id">
              <a :href="d.url" target="_blank" rel="noopener">{{ d.fileName }}</a>
              <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
            </li>
          </ul>
          <div v-else class="helper">No drawings uploaded.</div>
        </div>
      </div>

      <div class="helper" v-if="uploadFlash" style="margin-top:8px;">
        {{ uploadFlash }}
      </div>
    </div>

    <div class="row" style="margin-top:12px; align-items:center;">
      <button class="btn" :disabled="errors.length>0 || busy" @click="save">
        {{ busy ? 'Saving…' : 'Save' }}
      </button>
      <button class="btn secondary" @click="$emit('close')" style="margin-left:8px;" :disabled="busy">Cancel</button>

      <ul v-if="errors.length" style="margin-left:12px; color:#dc2626; font-size:13px;">
        <li v-for="(e,i) in errors" :key="'e'+i">• {{ e }}</li>
      </ul>

      <!-- Server-side errors should NOT block retry forever -->
      <ul v-if="serverErrors.length" style="margin-left:12px; color:#dc2626; font-size:13px;">
        <li v-for="(e,i) in serverErrors" :key="'se'+i">• {{ e }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
const PHONE_RE = /^\+?[0-9 \-()]{7,20}$/
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default {
  name:'CustomerForm',
  props:{ value:{type:Object, default:null} },

  data(){
    const base = {
      id:null,
      leadCode:'',
      name:'',
      phone:'',
      email:'',
      address:'',
      products:[],
      cause:'',
      causeOther:'',
      status:'follow_up',
      followUpDate:'',
      followUpNote:'',
      deadReason:'',
      deadOther:'',
      notes:''
    }

    const incoming = this.value ? Object.assign({}, this.value) : {}
    if (incoming.products && typeof incoming.products === 'string') {
      incoming.products = incoming.products.split(',').map(function(x){ return x.trim() }).filter(Boolean)
    }

    return {
      form: Object.assign({}, base, incoming),
      today: new Date().toISOString().slice(0,10),
      busy: false,
      serverErrors: [],

      // docs panel
      docsBusy: false,
      _refreshToken: 0,
      docs: { sitePhotos:0, measurements:0, drawings:0, docsOk:false, docsState:null, docsDeadline:null, docsOverdue:null },
      attachments: [],
      sel: { SITE_PHOTO:null, MEASUREMENT:null, DRAWING:null },
      uploadFlash: ''
    }
  },

  computed:{
    errors(){
      const e = []
      const f = this.form

      if (!f.name || !f.name.trim()) e.push('Name is required.')

      const hasPhone = !!(f.phone && f.phone.trim())
      const hasAddr  = !!(f.address && f.address.trim())
      if (!hasPhone && !hasAddr) e.push('Either Phone or Address is required.')

      if (hasPhone && !PHONE_RE.test(f.phone.trim())) e.push('Phone format looks invalid.')

      if (f.email && f.email.trim() && !EMAIL_RE.test(f.email.trim())) e.push('Email format looks invalid.')

      if (!Array.isArray(f.products) || f.products.length === 0)
        e.push('At least one product is required (kitchen/wardrobe/vanity).')

      if (!f.cause) e.push('Cause of lead is required.')
      if (f.cause === 'other' && !(f.causeOther && f.causeOther.trim()))
        e.push("Please describe 'Other' cause of lead.")

      if (f.status === 'follow_up') {
        if (!f.followUpDate) e.push('Follow-up date is required when status is Follow-up.')
        else {
          const t = new Date(this.today)
          const d = new Date(f.followUpDate)
          if (d < t) e.push('Follow-up date cannot be in the past.')
        }
      }

      if (f.status === 'dead') {
        if (!f.deadReason) e.push('Dead reason is required when status is Dead.')
        if (f.deadReason === 'other' && !(f.deadOther && f.deadOther.trim()))
          e.push("Please describe 'Other' dead reason.")
      }

      return e
    },

    docsOk(){
      if (this.docs && this.docs.docsState) return String(this.docs.docsState).toUpperCase() === 'COMPLETE'
      return !!(this.docs && this.docs.docsOk)
    },

    docsBadgeText(){
      if (this.docs && this.docs.docsState) {
        const s = String(this.docs.docsState).toUpperCase()
        if (s === 'COMPLETE') return 'Docs complete'
        if (s === 'OVERDUE') return 'Docs overdue'
        return 'Docs pending'
      }
      return this.docsOk ? 'Docs complete' : 'Docs missing'
    },

    docsBadgeClass(){
      if (this.docs && this.docs.docsState) {
        const s = String(this.docs.docsState).toUpperCase()
        if (s === 'COMPLETE') return 'badge--green'
        if (s === 'OVERDUE') return 'badge--red'
        return 'badge--amber'
      }
      return this.docsOk ? 'badge--green' : 'badge--red'
    },

    docsHintText(){
      if (this.docsOk) return ''
      if (this.docs && (this.docs.docsOverdue === true || String(this.docs.docsState||'').toUpperCase()==='OVERDUE')) {
        return 'This inquiry is past the 24-hour docs deadline. Upload required docs ASAP.'
      }
      return 'Upload Site Photos + Measurements. (You can do this now, even before converting to Inquiry.)'
    },

    docsHintColor(){
      if (this.docs && (this.docs.docsOverdue === true || String(this.docs.docsState||'').toUpperCase()==='OVERDUE')) return '#dc2626'
      return '#92400e'
    },

    photos(){
      return (this.attachments||[]).filter(function(a){ return a.docType==='SITE_PHOTO' })
    },
    measurements(){
      return (this.attachments||[]).filter(function(a){ return a.docType==='MEASUREMENT' })
    },
    drawings(){
      return (this.attachments||[]).filter(function(a){ return a.docType==='DRAWING' })
    }
  },

  watch:{
    'form.status': function(n){
      this.serverErrors = []
      if (n !== 'follow_up') {
        this.form.followUpDate = ''
        this.form.followUpNote = ''
      }
      if (n !== 'dead') {
        this.form.deadReason = ''
        this.form.deadOther = ''
      }
    },

    'form.id': function(){
      this.refreshDocs()
    }
  },

  async mounted(){
    if (this.form && this.form.id) this.refreshDocs()
  },

  methods:{
    hasProduct(tag){
      return Array.isArray(this.form.products) && this.form.products.indexOf(tag) > -1
    },

    toggleProduct(tag){
      const idx = this.form.products.indexOf(tag)
      if (idx > -1) this.form.products.splice(idx, 1)
      else this.form.products.push(tag)
      this.form.products = this.form.products.slice()
    },

    pickToday(){ this.form.followUpDate = this.today },

    addDays(n){
      const d = new Date()
      d.setDate(d.getDate() + n)
      this.form.followUpDate = d.toISOString().slice(0,10)
    },

    fixPastDate(){
      if (!this.form.followUpDate) return
      const t = new Date(this.today)
      const d = new Date(this.form.followUpDate)
      if (d < t) this.form.followUpDate = this.today
    },

    fmtDateTime(d){
      if (!d) return '—'
      try { return new Date(d).toLocaleString() } catch(e){ return String(d) }
    },

    serialize(){
      const d = Object.assign({}, this.form)
      d.products = (d.products || []).join(',')

      if (d.status !== 'follow_up') {
        d.followUpDate = null
        d.followUpNote = null
      } else {
        d.followUpNote = (d.followUpNote || '').trim() || null
      }

      if (d.status !== 'dead') {
        d.deadReason = null
        d.deadOther = null
      }

      delete d.leadCode
      return d
    },

    async save(){
      this.serverErrors = []
      if (this.errors.length) return

      const body = this.serialize()
      this.busy = true
      try{
        if (this.form.id) {
          const res = await this.$api.put('/leads/' + this.form.id, body)
          if (res && res.data && res.data.errors) {
            this.serverErrors = res.data.errors
            return
          }
          this.$emit('saved', Object.assign({}, this.form))
        } else {
          const res = await this.$api.post('/leads', body)
          if (res && res.data && res.data.errors) {
            this.serverErrors = res.data.errors
            return
          }

          const created = res && res.data ? res.data : null
          if (created && created.id) {
            this.form = Object.assign({}, this.form, created)
            await this.refreshDocs()
          }
          this.$emit('saved', created || Object.assign({}, this.form))
        }
      } catch(e){
        const d = e && e.response && e.response.data
        if (d && Array.isArray(d.errors)) this.serverErrors = d.errors
        else this.serverErrors = ['Save failed.']
      } finally {
        this.busy = false
      }
    },

    async refreshDocs(){
      if (!this.form || !this.form.id) return

      // prevent overlap: only the latest call wins
      const token = ++this._refreshToken
      this.uploadFlash = ''
      this.docsBusy = true

      try{
        const a = await this.$api.get(`/inquiries/${this.form.id}/attachments`)
        if (token !== this._refreshToken) return
        this.attachments = Array.isArray(a.data) ? a.data : []
      } catch(e){
        if (token !== this._refreshToken) return
        this.attachments = []
      }

      try{
        const s = await this.$api.get(`/inquiries/${this.form.id}/docs_status`)
        if (token !== this._refreshToken) return
        this.docs = s.data || { sitePhotos:0, measurements:0, drawings:0, docsOk:false }
      } catch(e){
        if (token !== this._refreshToken) return
        this.docs = { sitePhotos:0, measurements:0, drawings:0, docsOk:false }
      } finally {
        if (token === this._refreshToken) this.docsBusy = false
      }
    },

    pickFile(ev, type){
      const f = ev.target.files && ev.target.files[0]
      this.sel[type] = f || null
      this.uploadFlash = ''
    },

    async upload(type){
      if (!this.form || !this.form.id) return
      const f = this.sel[type]
      if (!f) return

      const toDataUrl = file => new Promise((res,rej)=>{
        const r = new FileReader()
        r.onload = function(){ res(r.result) }
        r.onerror = rej
        r.readAsDataURL(file)
      })

      this.uploadFlash = 'Uploading...'
      this.docsBusy = true
      try{
        const dataUrl = await toDataUrl(f)
        await this.$api.post(`/inquiries/${this.form.id}/attachments`, {
          dataUrl: dataUrl,
          filename: f.name,
          docType: type
        })
        this.uploadFlash = 'Uploaded ✓'
        this.sel[type] = null
        await this.refreshDocs()
      } catch(e){
        this.uploadFlash = 'Upload failed'
      } finally {
        this.docsBusy = false
      }
    }
  }
}
</script>

<style scoped>
.btn.small{ padding:6px 10px; font-size:12px; }

.badge{
  padding:2px 8px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
}
.badge--green{ background:#14532d; color:#dcfce7; border-color:#14532d }
.badge--red{ background:#7f1d1d; color:#fee2e2; border-color:#7f1d1d }
.badge--amber{ background:#92400e; color:#fff7ed; border-color:#92400e }

.mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
</style>
